"use client"

import { Children, isValidElement, type ReactNode } from "react"

import { cn } from "@coval/ui"
import Prism from "prismjs"

import "prismjs/components/prism-sql"

/**
 * Replaces Streamdown's default fenced-code renderer inside Sofia. It drops the
 * bordered card / language header / copy-download chrome (and the empty-line
 * caret artifacts) in favor of a plain, slightly-shaded block that sits in line
 * with the message text. SQL is highlighted with Prism (Streamdown's own Shiki
 * pass isn't wired up in this app); Prism's token classes are colored to the
 * Coval palette below.
 */

const BLOCK_CLASSES =
  "my-2 overflow-x-auto rounded-md bg-surface-2 p-3 font-mono text-xs leading-relaxed text-coval-text"

// Color Prism's `.token.*` classes to the panel's palette (light + dark).
const TOKEN_CLASSES =
  "[&_.token.keyword]:font-medium [&_.token.keyword]:text-sky-600 dark:[&_.token.keyword]:text-sky-400 " +
  "[&_.token.function]:text-violet-600 dark:[&_.token.function]:text-violet-400 " +
  "[&_.token.string]:text-emerald-600 dark:[&_.token.string]:text-emerald-400 " +
  "[&_.token.number]:text-amber-600 dark:[&_.token.number]:text-amber-400 " +
  "[&_.token.boolean]:text-amber-600 dark:[&_.token.boolean]:text-amber-400 " +
  "[&_.token.operator]:text-coval-text-muted [&_.token.punctuation]:text-coval-text-muted " +
  "[&_.token.comment]:italic [&_.token.comment]:text-coval-text-muted"

/** Flattens a React node tree to its plain text (Shiki spans or raw string). */
function nodeToText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(nodeToText).join("")
  }
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return nodeToText(node.props.children)
  }
  return ""
}

function getLanguage(codeElement: ReactNode): string | undefined {
  if (isValidElement<{ className?: string }>(codeElement)) {
    return /language-(\w+)/.exec(codeElement.props.className ?? "")?.[1]
  }
  return undefined
}

/**
 * Streamdown/react-markdown renders fenced code as `<pre><code>`. This overrides
 * the `pre`: it reads the code text out of its `<code>` child and re-renders a
 * clean, shaded block. Non-SQL blocks render plain (no highlight).
 */
export function CoviCodeBlock({ children }: { children?: ReactNode }) {
  const codeElement = Children.toArray(children)[0]
  const raw = nodeToText(codeElement).replace(/^\n+/, "").replace(/\s+$/, "")
  const language = getLanguage(codeElement)
  const isSql = language === undefined || language.toLowerCase() === "sql"
  const sqlGrammar = Prism.languages.sql

  if (!isSql || !sqlGrammar) {
    return <pre className={BLOCK_CLASSES}>{raw}</pre>
  }

  // Prism.highlight escapes the input, so only Prism's own token markup is
  // injected — the SQL text itself is HTML-escaped.
  const html = Prism.highlight(raw, sqlGrammar, "sql")
  return (
    <pre
      className={cn(BLOCK_CLASSES, TOKEN_CLASSES)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
